import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { BrandMark } from "@/components/BrandMark";
import { SITE, NAV_LINKS } from "@/content/site";

/**
 * Header – transparent oben, solid beim Scrollen.
 * Safe-Area (iPhone Notch/Dynamic Island) wird berücksichtigt.
 */
export const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      {/* Hintergrund füllt auch die Safe-Area (kein Spalt zum iPhone-Rand) */}
      <div
        className={`absolute inset-0 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          scrolled
            ? "border-b border-border/70 bg-[hsl(var(--brand-paper))]/95 shadow-[var(--shadow-header)] backdrop-blur-md opacity-100"
            : "border-b border-transparent bg-[hsl(var(--brand-paper))]/70 backdrop-blur-sm opacity-100"
        }`}
        aria-hidden="true"
      />

      <div className="site-container relative flex h-14 items-center justify-between gap-3 sm:h-16">
        <Link
          to="/"
          aria-label="Zur Startseite – Schweisstechnik Steyskal"
          data-testid="header-brand-link"
          className={`flex shrink-0 items-center leading-none rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            scrolled
              ? "opacity-100 translate-x-0 max-w-[120px]"
              : "opacity-0 -translate-x-3 max-w-0 overflow-hidden pointer-events-none"
          }`}
        >
          <BrandMark size={36} />
        </Link>

        <nav
          className={`hidden items-center gap-7 md:flex transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            scrolled ? "" : "ml-auto mr-6"
          }`}
          aria-label="Hauptnavigation"
          data-testid="header-nav"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`/#${link.id}`}
              data-testid={`header-link-${link.id}`}
              className="group relative py-2 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {link.label}
              <span className="absolute inset-x-0 -bottom-0.5 h-px origin-left scale-x-0 bg-[hsl(var(--brand-steelblue))] transition-transform duration-200 ease-out group-hover:scale-x-100" aria-hidden="true" />
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <a
            href={SITE.phoneHref}
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground"
          >
            <Phone className="h-3.5 w-3.5" />
            {SITE.phoneDisplay}
          </a>
          <Button asChild size="sm" className="rounded-full px-5 active:scale-[0.98]" data-testid="header-cta-button">
            <a href="/#kontakt">Offerte anfragen</a>
          </Button>
        </div>

        <div className="ml-auto flex items-center gap-1 md:hidden">
          <Button asChild variant="ghost" size="icon" aria-label="Anrufen" data-testid="header-mobile-call-button" className="h-10 w-10">
            <a href={SITE.phoneHref}>
              <Phone className="h-4 w-4" />
            </a>
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Menü öffnen" data-testid="mobile-menu-button" className="h-10 w-10">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-[hsl(var(--brand-paper))]">
              <SheetHeader className="text-left">
                <SheetTitle className="flex items-center leading-none">
                  <BrandMark size={48} />
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-8 flex flex-col" aria-label="Mobile Navigation">
                {NAV_LINKS.map((link) => (
                  <SheetClose asChild key={link.id}>
                    <a
                      href={`/#${link.id}`}
                      data-testid={`mobile-nav-link-${link.id}`}
                      className="border-b border-border/70 py-4 text-base font-medium text-foreground"
                    >
                      {link.label}
                    </a>
                  </SheetClose>
                ))}
              </nav>
              <div className="mt-8 flex flex-col gap-3">
                <SheetClose asChild>
                  <Button asChild className="rounded-full active:scale-[0.98]" data-testid="mobile-menu-cta">
                    <a href="/#kontakt">Offerte anfragen</a>
                  </Button>
                </SheetClose>
                <Button asChild variant="outline" className="rounded-full" data-testid="mobile-menu-call">
                  <a href={SITE.phoneHref}>
                    <Phone className="h-4 w-4" />
                    {SITE.phoneDisplay}
                  </a>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
